'use client';

import React from 'react';
import NextImage from 'next/image';
import { twMerge } from 'tailwind-merge';

// Define the predefined aspect ratios as specified in the issue
export type PredefinedAspectRatio =
  | '2/3'
  | '3/2'
  | '4/3'
  | '3/4'
  | '6/4'
  | '4/6'
  | '16/9'
  | '9/16'
  | '21/9'
  | '9/21'
  | '1/1';

interface ImageProps {
  src: string;
  alt: string;
  aspectRatio?: PredefinedAspectRatio;
  width?: string | number;
  height?: string | number;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'; // Kept for backward compatibility
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLImageElement>) => void;
  priority?: boolean;
  loading?: 'eager' | 'lazy';
}

const Image: React.FC<ImageProps> = ({
  src,
  alt,
  aspectRatio,
  width = 1440,
  height = 720,
  objectFit = 'cover',
  className = '',
  onClick,
  priority = false,
  loading = 'lazy',
  ...other
}) => {
  const numericWidth = typeof width === 'number' ? width : parseInt(width);
  let numericHeight = typeof height === 'number' ? height : parseInt(height);

  // если aspectRatio есть, переопределим высоту:
  if (aspectRatio && numericWidth) {
    const [w, h] = aspectRatio.split('/').map(Number);
    numericHeight = Math.round((numericWidth * h) / w);
  }

  const baseClasses = twMerge(
    'max-w-full',
    objectFit === 'cover' && 'object-cover',
    objectFit === 'contain' && 'object-contain',
    objectFit === 'fill' && 'object-fill',
    objectFit === 'none' && 'object-none',
    objectFit === 'scale-down' && 'object-scale-down',
    onClick && 'cursor-pointer'
  );

  const classes = twMerge(baseClasses, className);

  return (
    <NextImage
      src={src}
      alt={alt}
      width={numericWidth}
      height={numericHeight}
      className={classes}
      onClick={onClick}
      priority={priority}
      quality={100}
      loading={loading === 'eager' ? 'eager' : 'lazy'}
      {...other}
    />
  );
};

export default Image;
